import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Translations } from "./translations/en";

export type Language = "en" | "de" | "fr" | "uk" | "es";

// All locales (including German, the default market) are loaded on demand.
// German is no longer statically bundled — this keeps ~390 KB (raw) / ~95 KB
// gzip out of the critical path so first paint is not blocked on parsing the
// entire DE translation file. The boot screen (AppPreloader) covers the brief
// window until the active locale finishes loading.
const LOADERS: Record<Language, () => Promise<{ default: Translations }>> = {
  de: () => import("./translations/de"),
  en: () => import("./translations/en"),
  fr: () => import("./translations/fr"),
  uk: () => import("./translations/uk"),
  es: () => import("./translations/es"),
};

const LOADED: Partial<Record<Language, Translations>> = {};

const LANG_LABELS: Record<Language, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  uk: "Українська",
  es: "Español",
};

// Wrap translations so that accessing a not-yet-loaded key (during the brief
// window before the active locale chunk resolves) returns "" instead of
// throwing. This keeps every `t.x.y` access safe even when `t` is empty.
function makeSafeT(base: Translations): Translations {
  return new Proxy(base, {
    get(target, prop) {
      const val = (target as Record<string | symbol, unknown>)[prop];
      if (val && typeof val === "object") return makeSafeT(val as Translations);
      if (val === undefined) return "" as unknown as Translations;
      return val as Translations;
    },
  }) as Translations;
}

const STORAGE_KEY = "kaufyard_lang";

/**
 * `in` also matches inherited keys, so `"constructor" in LANG_LABELS` is true
 * and a tampered or legacy stored value would be accepted as a language —
 * selecting a locale that has no strings at all.
 */
function isLang(v: unknown): v is Language {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(LANG_LABELS, v);
}

function getInitialLang(): Language {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* storage blocked — fall through to the browser language */
  }
  if (isLang(stored)) return stored;
  const browserLang = navigator.language?.slice(0, 2);
  if (isLang(browserLang)) return browserLang;
  return "de";
}

interface LanguageContextValue {
  lang: Language;
  setLang: (l: Language) => void;
  t: Translations;
  languages: { code: Language; label: string }[];
  /** False only until the first locale chunk resolves. */
  ready: boolean;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const initialLang = getInitialLang();
  const [lang, setLangState] = useState<Language>(initialLang);
  const [, forceRender] = useState(0);

  // Last locale whose chunk has actually resolved. Null only before the very
  // first chunk arrives.
  const [loadedLang, setLoadedLang] = useState<Language | null>(() =>
    LOADED[initialLang] ? initialLang : null,
  );
  // Set when a chunk fails to load. Gating children forever would leave a blank
  // screen, so we degrade to an empty dictionary instead — some strings come out
  // blank, but the app stays usable and RouteErrorBoundary still covers pages
  // that crash on a missing title.
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    if (LOADED[lang]) {
      setLoadedLang(lang);
      return;
    }
    let cancelled = false;
    LOADERS[lang]()
      .then((mod) => {
        LOADED[lang] = mod.default;
        if (cancelled) return;
        setLoadedLang(lang);
        forceRender((n) => n + 1);
      })
      .catch(() => {
        if (!cancelled) setGaveUp(true);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  // Never hand out an empty dictionary. Pages interpolate `t.x.y` straight into
  // <title>, and an empty `t` yields `undefined` there — react-helmet-async
  // throws ("expects a string as a child of <title>") and takes the whole page
  // down. Falling back to the previously loaded locale keeps a language switch
  // crash-free and flash-free; before any chunk exists we gate `children`.
  const activeLang = loadedLang ?? lang;
  const translations = makeSafeT(LOADED[activeLang] ?? ({} as Translations));
  const open = loadedLang !== null || gaveUp;

  const setLang = useCallback((l: Language) => {
    setLangState(l);
    // A blocked write must not take the language switch — and the app with it —
    // down. The choice still applies for this session.
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* not fatal */
    }
    document.documentElement.lang = l;
    // Sync to profile so the Telegram bot replies in the user's language.
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        supabase.from("profiles").update({ language: l }).eq("user_id", data.user.id).then(() => {});
      }
    });
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const languages = Object.entries(LANG_LABELS).map(([code, label]) => ({
    code: code as Language,
    label,
  }));

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations, languages, ready: open }}>
      {open ? children : null}
    </LanguageContext.Provider>
  );
};

// Fallback used only if the context is somehow unavailable (e.g. Vite HMR
// context-identity split). Empty `t` here is acceptable — this path is hit only
// during dev hot-reload edge cases, never in production first paint.
const FALLBACK: LanguageContextValue = {
  lang: "de",
  setLang: () => {},
  t: makeSafeT({} as Translations),
  languages: Object.entries(LANG_LABELS).map(([code, label]) => ({
    code: code as Language,
    label,
  })),
  ready: true,
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  return ctx ?? FALLBACK;
};
